'use client';
import React, { useLayoutEffect, useEffect, useRef, useState } from 'react';
import GameMap from '@wareena/ui/game/map';
import { SideId } from '@wareena/grid-battles-entity/game/codebook/side';
import { calcMapDimensions } from '@wareena/game-algorithms';
import { getTerrainHex } from '../../terrain/index.js';
import createStateMachine from '@wareena/grid-battles-engine/gameStateMachine';
import { attack } from '@wareena/grid-battles-engine/gameStateMachine/request';
import { RequestType } from '@wareena/grid-battles-engine/gameStateMachine/request/types';
import getDiceRollResults from '@wareena/grid-battles-engine/gameStateMachine/helpers/battle/getDiceRollResults';
import createStateComponents from '../../stateComponents/index.js';
import UnitLayer from '../UnitLayer.js';
import GameOverMessage from '../../GameOverMessage.js';
import { getStateWithUpdatedPosition } from '@wareena/grid-battles-engine/services/gameStateUtils';
import ResponsiveSvg from '@wareena/ui/game/map/ResponsiveSvg';
import useMapSizeController from './useMapSizeController.js';
import NonResponsiveSvg from './NonResponsiveSvgMap.js';
import GameHeaderWithInstructions from './GameHeaderWithInstructions.js';
import CombatView from './CombatView.js';
import MapSizeButtons from './MapSizeButtons.js';
import { useGameContext } from './GameContext.js';
import useAgent from './useAgent.js';
export default function GameBoard({ strategyIngredients = [], onStateChange, navigationButton, initialAgentRequests = [], }) {
    const { game, setGame, scenario, hexProperties, rules, playerInfo, loggedInUserId, wasGameOverMessageHidden, setWasGameOverMessageHidden, combatDetail, setCombatDetail, } = useGameContext();
    const agentPlayer = playerInfo.opponentPlayer;
    const machine = createStateMachine({
        game,
        scenario,
        hexProperties,
        onRequestSuccess,
    });
    const { agentRequestsContainer, setIsAgentPaused } = useAgent(initialAgentRequests, agentPlayer, machine, strategyIngredients);
    const mapSizeController = useMapSizeController(10 * scenario.width);
    const [winDimensions, setWinDimensions] = useState({ width: 0, height: 0 });
    useEffect(() => {
        function listener() {
            setWinDimensions({ width: window.innerWidth, height: window.innerHeight });
        }
        addEventListener('resize', listener);
        return () => removeEventListener('resize', listener);
    }, []);
    const mapDimensions = calcMapDimensions(scenario.width, scenario.height, hexProperties, scenario.hasLinesOfSameLength);
    const winner = game.state.stateName === 'gameOver'
        ? game.state.winner === SideId.A
            ? playerInfo.playerA
            : playerInfo.playerB
        : null;
    function onRequestSuccess({ nextState, request }) {
        const nextGame = {
            ...game,
            state: nextState,
            lastCommandId: game.lastCommandId + 1,
        };
        setGame(nextGame);
        if (request.type === RequestType.Attack && request.payload.userId === agentPlayer.id) {
            const combat = getLastResolvedCombat(nextState.combats ?? []);
            if (combat) {
                setCombatDetail((prevDetail) => {
                    return {
                        ...prevDetail,
                        isVisible: true,
                        combatId: combat.id,
                    };
                });
                setIsAgentPaused(true);
            }
        }
        if (onStateChange) {
            onStateChange(nextState, request, agentRequestsContainer.getAgentRequests());
        }
    }
    useEffect(() => {
        if (combatDetail.isVisible) {
            setIsAgentPaused(true);
        }
        else {
            setIsAgentPaused(false);
        }
    }, [combatDetail.isVisible]);
    const { ActiveLayer, Instruction, ActionPanel, getUnitReplacement } = createStateComponents({
        stateMachine: machine,
        state: game.state,
        game,
        scenario,
        loggedInPlayerId: loggedInUserId,
        hexProperties,
    });
    function showCombatDetail(currentCombatId) {
        setIsAgentPaused(true);
        setCombatDetail((prevDetail) => {
            return {
                ...prevDetail,
                isVisible: true,
                combatId: currentCombatId,
            };
        });
    }
    function hideCombatDetail() {
        setIsAgentPaused(false);
        setCombatDetail((prevDetail) => {
            return {
                ...prevDetail,
                isVisible: false,
            };
        });
    }
    function handleShowCombatDetail() {
        const currentCombat = game.state.combats?.find((c) => c.id === game.state.currentCombatId);
        if (!currentCombat || !currentCombat.attackerId || !currentCombat.defenderId) {
            return;
        }
        const diceRollResults = getDiceRollResults(game.state, rules);
        if (!diceRollResults) {
            return;
        }
        showCombatDetail(game.state.currentCombatId);
        const nextState = machine.executeRequest(attack(loggedInUserId, diceRollResults), game.state);
        setGame((prev) => {
            return {
                ...prev,
                state: nextState,
                lastCommandId: prev.lastCommandId + 1,
            };
        });
    }
    const stateForBoldCalculation = game.state.stateName === 'movingUnit'
        ? getStateWithUpdatedPosition(game.state)
        : game.state;
    const isGameOver = game.state.stateName === 'gameOver';
    const isGameOverMessageVisible = isGameOver && !combatDetail.isVisible && !wasGameOverMessageHidden;
    const SvgElement = mapSizeController.isControlled ? NonResponsiveSvg : ResponsiveSvg;
    function handleCloseGameOverMessage() {
        setWasGameOverMessageHidden(true);
    }
    const actionPanelRef = useRef(null);
    const gameMapContainerRef = useRef(null);
    const [actionPanelStyleProps, setActionPanelStyleProps] = useState({});
    useLayoutEffect(() => {
        const svgRect = mapSizeController.svgElementRef.current?.getBoundingClientRect();
        const actionPanelRect = actionPanelRef.current?.getBoundingClientRect();
        const gameMapContainerRect = gameMapContainerRef.current?.getBoundingClientRect();
        if (window.matchMedia('(min-width: 1024px)').matches &&
            svgRect &&
            actionPanelRect &&
            gameMapContainerRect) {
            setActionPanelStyleProps({
                position: 'absolute',
                margin: 0,
                top: Math.max(0, (Math.min(svgRect.height, gameMapContainerRect.height) -
                    actionPanelRect.height) /
                    2),
                left: Math.max(0, (Math.min(svgRect.width, gameMapContainerRect.width) - actionPanelRect.width) /
                    2),
            });
        }
        else {
            setActionPanelStyleProps({});
        }
    }, [mapSizeController.size, winDimensions, ActionPanel !== undefined, combatDetail.isVisible]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { id: "game-screen" },
            React.createElement(GameHeaderWithInstructions, { navigationButton: navigationButton, instruction: React.createElement(Instruction, null), game: game, scenario: scenario, playerInfo: playerInfo }),
            !isGameOver && ActionPanel ? (React.createElement("div", { className: "action-panel", ref: actionPanelRef, style: actionPanelStyleProps },
                React.createElement(ActionPanel, { onShowCombatDetail: handleShowCombatDetail }))) : null,
            combatDetail.isVisible ? (React.createElement(CombatView, { combatDetail: combatDetail, game: game, rules: rules, scenario: scenario, actionPanelRef: actionPanelRef, hideCombatDetail: hideCombatDetail, actionPanelStyleProps: actionPanelStyleProps })) : null,
            isGameOverMessageVisible ? (React.createElement("div", { className: "game-over-message" },
                React.createElement(GameOverMessage, { isCurrentPlayer: winner.id === loggedInUserId, winner: winner, onClose: handleCloseGameOverMessage }))) : null,
            React.createElement("div", { className: "game-map-container", ref: gameMapContainerRef },
                React.createElement(SvgElement, { width: mapDimensions.width, height: mapDimensions.height, svgProps: {
                        style: getSvgStyleProps(isGameOver, mapSizeController.isControlled, mapSizeController.size),
                    }, ref: mapSizeController.svgElementRef },
                    React.createElement(GameMap, { hexProperties: hexProperties, map: scenario.hexes, getTerrainHex: getTerrainHex }),
                    React.createElement(UnitLayer, { hexProperties: hexProperties, units: game.state.units, getUnitReplacement: getUnitReplacement, getBoldStatus: (unit) => {
                            return rules.isUnitBold(unit, stateForBoldCalculation);
                        } }),
                    React.createElement(ActiveLayer, { onShowCombatDetail: handleShowCombatDetail }))),
            React.createElement(MapSizeButtons, { increaseSize: mapSizeController.increaseSize, decreaseSize: mapSizeController.decreaseSize, resetSize: mapSizeController.resetSize }))));
}
function getSvgStyleProps(isGameOver, isMapSizeControlled, mapSize) {
    const svgStyleProps = {};
    if (isGameOver) {
        svgStyleProps.filter = 'grayscale(80%)';
    }
    if (isMapSizeControlled) {
        svgStyleProps.width = `${mapSize}px`;
    }
    return svgStyleProps;
}
function getLastResolvedCombat(combats) {
    for (let i = (combats?.length ?? 0) - 1; i >= 0; i -= 1) {
        const combat = combats?.[i];
        if (combat?.wasResolved) {
            return combat;
        }
    }
    return null;
}
